import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Camera, ChevronLeft, ChevronRight } from "lucide-react";
import { SponsorBanner } from "./SponsorBanner";
import { SponsorCard } from "./SponsorCard";
import { LISTING_STATUS_LABELS } from "./constants";

const HERO_IMAGE_ANIMATION = { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.8 } };

export function HeroSection({ listing, ordered, hero, heroIndex, setHeroIndex, fileUrl, stats, onViewPhoto, onRequestShowing }) {
  return (
    <section className="relative h-[52vh] sm:h-[62vh] lg:h-[500px] w-full overflow-hidden bg-[#111820] mt-5">
      <AnimatePresence mode="wait" initial={false}>
        {hero && (
          <motion.img key={hero.id} src={fileUrl(hero)} alt={listing.full_address || listing.project_name} onClick={() => onViewPhoto(heroIndex)}
            {...HERO_IMAGE_ANIMATION}
            className="absolute inset-0 h-full w-full object-cover cursor-pointer" data-testid="public-listing-hero-image" />
        )}
      </AnimatePresence>
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent pointer-events-none" />
      {ordered.length > 1 && (
        <>
          <button data-testid="public-listing-hero-prev" onClick={() => setHeroIndex((i) => (i - 1 + ordered.length) % ordered.length)} className="absolute left-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/30 hover:bg-black/50 grid place-items-center backdrop-blur-sm transition-colors text-white"><ChevronLeft size={18} /></button>
          <button data-testid="public-listing-hero-next" onClick={() => setHeroIndex((i) => (i + 1) % ordered.length)} className="absolute right-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/30 hover:bg-black/50 grid place-items-center backdrop-blur-sm transition-colors text-white"><ChevronRight size={18} /></button>
          <div data-testid="public-listing-hero-counter" className="absolute top-5 right-5 rounded-full bg-black/40 backdrop-blur-sm text-white text-xs font-mono px-3 py-1">{heroIndex + 1} / {ordered.length}</div>
        </>
      )}
      <div className="absolute inset-x-0 bottom-0 p-5 sm:p-8 lg:p-10 text-white pointer-events-none">
        {listing.listing_status && <span data-testid="public-listing-status" className="inline-block rounded-full bg-[#C7A361] text-[#111820] text-[11px] font-semibold uppercase tracking-[0.1em] px-3 py-1 mb-3">{LISTING_STATUS_LABELS[listing.listing_status] || listing.listing_status}</span>}
        <h1 data-testid="public-listing-title" className="font-serif-display text-2xl sm:text-4xl">{listing.full_address || listing.project_name}</h1>
        <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2">
          {listing.price && <div data-testid="public-listing-price" className="font-serif-display text-xl sm:text-2xl text-[#C7A361]">{listing.price}</div>}
          {stats.map((s) => (
            <div key={s.label} data-testid={`public-listing-stat-${s.label.toLowerCase().replace(/\s/g, "-")}`} className="flex items-center gap-1.5 text-sm sm:text-base"><s.icon size={16} className="text-[#C7A361]" />{s.value} {s.label}</div>
          ))}
        </div>
        <div className="mt-5 flex flex-wrap gap-3 pointer-events-auto">
          <button data-testid="public-listing-schedule-showing" onClick={onRequestShowing} className="rounded-full bg-[#C7A361] text-[#111820] text-sm font-semibold px-5 py-2.5 hover:brightness-95 transition">Request a Showing</button>
          {ordered.length > 0 && <a href="#gallery" data-testid="public-listing-view-photos" className="inline-flex items-center gap-1.5 rounded-full border border-white/50 text-white text-sm font-medium px-5 py-2.5 hover:bg-white/10 transition"><Camera size={15} />View Photos</a>}
        </div>
      </div>
    </section>
  );
}

export function PhotosSection({ ordered, fileUrl, onViewPhoto, slug, withQS }) {
  return (
    <div id="gallery" className="scroll-mt-24">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-serif-display text-xl sm:text-2xl text-[#202833]">Photos</h2>
        {ordered.length > 8 && <Link to={withQS(`/listing/${slug}/details`)} data-testid="public-listing-view-all-photos" className="text-sm font-medium text-[#C7A361] hover:underline">View All Photos</Link>}
      </div>
      {ordered.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {ordered.slice(0, 8).map((file, i) => (
            <button key={file.id} data-testid={`public-listing-photo-${file.id}`} onClick={() => onViewPhoto(i)} className="aspect-square overflow-hidden rounded-lg bg-[#F8F7F4]">
              <img src={fileUrl(file)} alt="" loading="lazy" className="h-full w-full object-cover hover:scale-105 transition-transform duration-300" />
            </button>
          ))}
        </div>
      ) : <p className="text-sm text-[#667085]">Photos coming soon.</p>}
    </div>
  );
}

export function LocalServicesSection({ localServices, slug, withQS, onInquire }) {
  if (!localServices.length) return null;
  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-serif-display text-xl sm:text-2xl text-[#202833]">Local Services for Your Next Move</h2>
        <Link to={withQS(`/listing/${slug}/sponsors`)} data-testid="public-listing-view-all-sponsors" className="text-sm font-medium text-[#C7A361] hover:underline whitespace-nowrap">View All Sponsors</Link>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {localServices.map((ad) => <SponsorCard key={ad.id} advertiser={ad} onInquire={onInquire} compact />)}
      </div>
    </div>
  );
}

export function PromoSection({ contact, onRequestShowing }) {
  if (!contact?.name) return null;
  return (
    <section className="bg-[#F8F7F4] border-y border-[#E7E4DE] py-14 sm:py-20">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 text-center">
        <h2 className="font-serif-display text-2xl sm:text-3xl text-[#202833]">Thinking About Buying or Selling?</h2>
        <div className="mt-6 flex flex-col items-center gap-3">
          {contact.photo_url ? <img src={contact.photo_url} alt={contact.name} className="h-16 w-16 rounded-full object-cover" /> : <div className="h-16 w-16 rounded-full bg-white border border-[#E7E4DE] grid place-items-center font-serif-display text-xl text-[#667085]">{contact.name[0]}</div>}
          <p className="font-serif-display text-lg text-[#202833]">{contact.name}</p>
          {contact.company && <p className="text-sm text-[#667085]">{contact.company}</p>}
        </div>
        <button data-testid="public-listing-promo-cta" onClick={onRequestShowing} className="mt-6 rounded-full bg-[#C7A361] text-[#111820] text-sm font-semibold px-6 py-3 hover:brightness-95 transition">Get in Touch</button>
      </div>
    </section>
  );
}


export function SponsorBannerSlot({ advertiser, onInquire, className }) {
  if (!advertiser) return null;
  return <div className={className}><SponsorBanner advertiser={advertiser} onInquire={onInquire} /></div>;
}
